import { useCallback } from "react";
import {
    Box, Card, CardActions, CardContent, CardHeader,
    CardMedia, IconButton, ToggleButton, Typography
} from "@mui/material"
import { ReferenceField, useGetIdentity, useRecordContext } from "react-admin";
import fileDownload from "js-file-download"
import { Moderation, Photo } from "../types";
import { MainPhotoSelectCheckbox } from "./MainPhotoSelectCheckbox";
import { ModerateControl } from "./ModerateControl";
import { VideoPlayer } from "./VideoPlayer";

import { get } from "lodash";
import { photoUrl, sendEvent } from "@/src/services";
import { DateTimeField } from "@/src/components";


import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import TelegramIcon from '@mui/icons-material/Telegram';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';

const sxMedia = {
    height: 320,
    backgroundSize: "contain"
}

interface Props {
    photo: Photo,
    onItemSelect?: () => void
    onUpdate?: (data: Partial<Photo>) => void
}


export const PhotoCard = ({ photo, onItemSelect, onUpdate }: Props) => {

    const { identity, isPending } = useGetIdentity()
   
    const handleDownloadPhoto = useCallback(async () => {
        const res = await fetch(photoUrl(photo, "full"));
        const blob = await res.blob();
        fileDownload(blob, `${photo.id}.jpeg`);
    }, [photo])

    const handlePublishPhoto = useCallback(async () => {
        sendEvent("photo-event", "publish", { photoId: photo.id })
    }, [photo])

    const handleApproveClick = useCallback(() => {
        if (identity && photo.moderation) {
            onUpdate?.({ approved: true, approvedAt: new Date(), approvedBy: identity.id.toString() })
        }
    }, [photo, onUpdate, identity])

    const handleModerateUpdate = useCallback((newValue: Moderation) => {
        if (identity) {
            const approval = { approved: !!newValue, approvedAt: new Date(), approvedBy: identity.id.toString() };
            onUpdate?.({ moderation: newValue, moderatedAt: new Date(), ...approval })
        }
    }, [onUpdate, identity])

    const mediatype = photo.mimetype.split('/')[0];

    return (
        <Card key={photo.id} >
            <CardHeader
                title={
                    <ReferenceField label="User" source="user" reference="user" record={photo}>
                        <UserField />
                    </ReferenceField>
                }
                subheader={<>Created: <DateTimeField source="createdAt" record={photo} /></>}

                action={<Box m={1} gridTemplateColumns={"1fr 1fr"} display={"grid"}>

                    <IconButton onClick={() => handleDownloadPhoto()} size="small">
                        <CloudDownloadIcon />
                    </IconButton>
                    <IconButton href={photoUrl(photo, "full")} target="_blank" size="small">
                        <OpenInNewIcon />
                    </IconButton>
                    <IconButton onClick={() => handlePublishPhoto()} size="small" title="publish to telegram">
                        <TelegramIcon />
                    </IconButton>
                </Box>}

            />
            {mediatype === 'image' &&
                <CardMedia image={photoUrl(photo, 'normal')} sx={sxMedia}
                    onClick={() => {
                        onItemSelect?.()
                    }}
                />
            }
            {mediatype === 'video' &&
                <Box sx={sxMedia} >
                    <VideoPlayer media={photo} />
                </Box>
            }
            <CardContent sx={{ padding: "16px 32px" }} >
                <MainPhotoSelectCheckbox photo={photo} />

                <Typography variant="body2">Created: <DateTimeField source="createdAt" record={photo} /></Typography>

                <Typography component="div">
                    {photo.moderation
                        ? <div>{photo.moderation} at <DateTimeField source='moderatedAt' record={photo} /></div>
                        : <div>moderation: NA</div>}
                </Typography>
                <Typography>
                    {photo.reasons.join(",")}
                </Typography>

            </CardContent>
            {!isPending &&
                <CardActions sx={{ flexDirection: "column" }}>
                    <ModerateControl value={photo.moderation}
                        onChange={handleModerateUpdate} />
                    <Box mt={1}>
                        <ToggleButton selected={photo.approved} disabled={!photo.moderation} onChange={handleApproveClick} value={""}>
                            {photo.approved ? <CheckBoxOutlinedIcon /> : <CheckBoxOutlineBlankOutlinedIcon />} Approved
                        </ToggleButton>
                    </Box>
                </CardActions>}

        </Card>
    )
}


const UserField = () => {

    const userRecord = useRecordContext()
    const { deleted, adminBlocked, profileValid, settings = {} } = userRecord as any
    const { accountVisibility } = settings as any;

    const disable = deleted || adminBlocked || !profileValid
        || accountVisibility === "deactivated" || accountVisibility === "hidden"

    return (
        <Box border={"1px #ccc solid"} px={1} py={0.5} borderRadius={"8px"}>
            <Typography variant="body1" style={{ textDecoration: disable && "line-through" }}>
                {get(userRecord, "profile.nickname")}
            </Typography>
            <Typography variant="subtitle2">{get(userRecord, "profile.gender")}, {get(userRecord, "profile.age")}</Typography>
        </Box>
    )

}



